import React, {
    Dispatch,
    MutableRefObject,
    SetStateAction,
    useCallback,
    useMemo,
    useRef
} from 'react';
import styled from 'styled-components';
import ReactQuill, { Quill } from 'react-quill-new';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import { Mutation } from '@graphql/types';
import { UPLOAD_TEXT_IMG } from '@graphql/mutations';
import { IMAGE_LIMIT_SIZE_3MB } from '@lib/constant';
import theme from '@styles/theme';
import CustomToolBar from '@components/share/Editor/CustomToolBar';

type TextEditorProps = {
    width?: number;
    margin?: string;
    title?: string;
    contents: string;
    setContents: Dispatch<SetStateAction<string>>;
    placeholder?: string;
};

const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'indent',
    'color',
    'background',
    'align',
    'link',
    'image',
    'formula',
    'code',
    'code-block',
    'script',
    'blockquote',
    'direction'
];

const TextEditor = ({
    width = 630,
    margin,
    title,
    contents,
    setContents,
    placeholder = '상세 내용 입력'
}: TextEditorProps) => {
    const quillRef = useRef<ReactQuill | null>(
        null
    ) as MutableRefObject<ReactQuill>;

    const modules = useMemo(
        () => ({
            toolbar: {
                container: '#text-editor-toolbar',
                clipboard: {
                    matchVisual: false
                },
                imageResize: {
                    parchment: Quill.import('parchment'),
                    modules: ['Resize', 'DisplaySize']
                },
                history: {
                    // 사용자 편집 history
                    delay: 500,
                    maxStack: 100,
                    userOnly: true
                },
                handlers: {
                    image: async () => {
                        const input = document.createElement('input');
                        const type = (
                            document.getElementById(
                                'uploadType'
                            ) as HTMLInputElement
                        )?.value;
                        let exceedsSizeLimit = false;

                        input.setAttribute('type', 'file');
                        input.setAttribute('multiple', 'multiple');
                        input.setAttribute('accept', 'image/*');
                        input.addEventListener('change', () => {
                            try {
                                const files = input.files;
                                const fileList = Array.from(files as FileList);

                                fileList.forEach((file: File) => {
                                    if (file.size > IMAGE_LIMIT_SIZE_3MB) {
                                        toast.error(
                                            '업로드 가능한 최대 용량은 3MB 입니다.'
                                        );
                                        exceedsSizeLimit = true;
                                    }
                                });
                                if (!exceedsSizeLimit) {
                                    // 3MB 이하의 이미지만 실행
                                    handleImageUpload(
                                        files as FileList,
                                        type
                                    ).then();
                                }
                            } catch (err) {
                                console.error(err);
                            }
                        });
                        input.click();
                    },
                    blockquote: () =>
                        quillRef.current
                            ?.getEditor()
                            .format(
                                'blockquote',
                                !quillRef.current?.getEditor().getFormat()
                                    .blockquote
                            ),
                    undo: () => quillRef.current?.getEditor().history.undo(),
                    redo: () => quillRef.current?.getEditor().history.redo(),
                    clean: () => {
                        if (
                            window.confirm('작성된 내용을 모두 지우시겠습니까?')
                        )
                            setContents('');
                    }
                }
            }
        }),
        []
    );

    const [uploadTextImg] =
        useMutation<Pick<Mutation, 'uploadTextImg'>>(UPLOAD_TEXT_IMG);

    const handleImageUpload = useCallback(
        async (files: FileList, type: string) => {
            const quill = quillRef.current;

            if (!files || files.length === 0) {
                // 파일 선택이 취소된 경우 사용자에게 알려주기
                toast.error('파일이 선택되지 않았습니다.');
                return;
            }

            if (quill) {
                try {
                    const { data } = await uploadTextImg({
                        variables: {
                            imgs: files
                        }
                    });
                    const editor = quill.getEditor();
                    const range = editor.getSelection(true);

                    // range 가 없을 경우 삽입할 대상의 위치를 찾지 못해 이미지가 삽입되지 않음
                    if (range && data?.uploadTextImg) {
                        const images =
                            (data.uploadTextImg as unknown as string[]) || [];
                        console.log(data.uploadTextImg);
                        images.forEach((img, index) => {
                            editor.insertEmbed(
                                range.index + index,
                                'image',
                                img
                            );
                        });
                    } else {
                        toast.error(
                            '에디터에 포커스를 맞추고 다시 시도해주세요.'
                        );
                    }
                } catch (error) {
                    toast.error(
                        '이미지 업로드 중 오류가 발생했습니다. 다시 시도해주세요.'
                    );
                }
            }
        },
        [IMAGE_LIMIT_SIZE_3MB, quillRef]
    );

    return (
        <Wrapper $width={width} $margin={margin}>
            {title && <Title>{title}</Title>}
            <CustomToolBar />
            <Editor
                theme="snow"
                ref={(element: any) =>
                    element ? (quillRef.current = element) : null
                }
                modules={modules}
                formats={formats}
                value={contents}
                onChange={setContents}
                placeholder={placeholder}
            />
        </Wrapper>
    );
};

export default TextEditor;

const Wrapper = styled.div<{ $width: number; $margin?: string }>`
    width: ${({ $width }) => ($width ? $width : 630)}px;
    min-height: 600px;
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
`;
const Title = styled.div`
    font-size: 14px;
    margin-bottom: 8px;
`;
const Editor = styled(ReactQuill)`
    // React Quill Style
    // font family
    h1 {
        font-size: 32px !important;
        font-weight: bold !important;
    }

    h2 {
        font-size: 24px !important;
        font-weight: bold !important;
    }

    h3 {
        font-size: 19px !important;
        font-weight: bold !important;
    }

    h4 {
        font-size: 16px !important;
        font-weight: bold !important;
    }

    h5 {
        font-size: 12px !important;
        font-weight: bold !important;
    }

    h6 {
        font-size: 10px !important;
        font-weight: bold !important;
    }

    em {
        font-style: italic !important;
    }

    b,
    strong {
        font-weight: bold !important;
    }

    .ql-container,
    .ql-editor {
        height: 580px !important;
        font-size: 16px;
        font-family: PretendardMedium, sans-serif !important;
    }

    .ql-container.ql-snow {
        border-radius: 4px;
        border: 1px solid ${theme.colors.ultraLightGrayBorder};
    }

    .ql-snow .ql-font .ql-picker {
        width: 130px !important;
    }

    .ql-editor.ql-blank::before {
        font-size: 14px;
        font-style: normal;
        font-family: PretendardRegular, sans-serif;
        color: ${theme.colors.deepGrayFontColor};
    }

    .ql-editor p {
        width: 100%;
        word-break: break-all;
    }

    .ql-picker {
        &.ql-size {
            .ql-picker-label,
            .ql-picker-item {
                &::before {
                    content: attr(data-value) !important;
                }
            }
        }
    }

    .ql-snow
        .ql-picker.ql-font
        .ql-picker-label[data-value='AppleSDGothicNeo']::before,
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-item[data-value='AppleSDGothicNeo']::before {
        content: 'AppleSDGothicNeo';
        font-family: AppleSDGothicNeoMedium, sans-serif !important;
    }

    .ql-font-AppleSDGothicNeo {
        font-family: AppleSDGothicNeoMedium, sans-serif !important;
    }

    // Nanum Gothic
    span.ql-font-NanumGothic,
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-label[data-value='NanumGothic']::before,
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-item[data-value='NanumGothic']::before {
        content: 'NanumGothic';
        font-family: 'Nanum Gothic', sans-serif !important;
    }

    .ql-font-AppleSDGothicNeo {
        font-family: AppleSDGothicNeoMedium, sans-serif !important;
    }

    // pretendard
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-label[data-value='pretendard']::before,
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-item[data-value='pretendard']::before {
        content: 'pretendard';
        font-family: PretendardMedium, sans-serif !important;
    }

    .ql-font-pretendard {
        font-family: PretendardMedium, sans-serif !important;
    }

    // sans-serif
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-label[data-value='sans-serif']::before,
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-item[data-value='sans-serif']::before {
        content: 'sans-serif';
        font-family: sans-serif !important;
    }

    .ql-font-sans-serif {
        font-family: sans-serif !important;
    }

    // monospace
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-label[data-value='monospace']::before,
    .ql-snow
        .ql-picker.ql-font
        .ql-picker-item[data-value='monospace']::before {
        content: 'monospace';
        font-family: monospace !important;
    }

    .ql-font-monospace {
        font-family: monospace !important;
    }

    // font size
    .ql-size-12px,
    .ql-size span[data-value='12px']::before {
        font-size: 12px !important;
    }

    .ql-size-14px,
    .ql-size span[data-value='14px']::before {
        font-size: 14px !important;
    }

    .ql-size-15px,
    .ql-size span[data-value='15px']::before {
        font-size: 15px !important;
    }

    .ql-size-16px,
    .ql-size span[data-value='16px']::before {
        font-size: 16px !important;
    }

    .ql-size-18px,
    .ql-size span[data-value='18px']::before {
        font-size: 18px !important;
    }

    .ql-size-20px,
    .ql-size span[data-value='20px']::before {
        font-size: 20px !important;
    }

    .ql-size-22px,
    .ql-size span[data-value='22px']::before {
        font-size: 22px !important;
    }

    .ql-size-24px,
    .ql-size span[data-value='24px']::before {
        font-size: 24px !important;
    }

    .ql-size-26px,
    .ql-size span[data-value='26px']::before {
        font-size: 26px !important;
    }

    .ql-size-28px,
    .ql-size span[data-value='28px']::before {
        font-size: 28px !important;
    }

    .ql-size-30px,
    .ql-size span[data-value='30px']::before {
        font-size: 30px !important;
    }

    .ql-size-32px,
    .ql-size span[data-value='32px']::before {
        font-size: 32px !important;
    }

    // Bold
    .ql-editor strong {
        font-weight: bold !important;
    }

    .ql-editor em {
        font-style: italic !important;
    }

    .ql-align-right {
        text-align: right !important;
    }

    .ql-align-center {
        text-align: center !important;
    }
`;
